import React from 'react';

import {
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import { AxiosResponse } from 'axios';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { GLMFlowDirection, useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { UseInfiniteQueryResult } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { parseRecordMessage } from 'utils/glmRecordMessage';

dayjs.extend(utc);
dayjs.extend(timezone);

type RecordStatus = 'pending' | 'success' | 'no_op' | 'failure';

type FlowRecord = {
  athleteId: string | null;
  athleteFirstName: string | null;
  athleteLastName: string | null;
  athleteEmail: string | null;
  status: RecordStatus | string;
  message: string | null;
  updatedAt: string;
};

export type UseRecordsFn = (
  flowId: string | null,
) => UseInfiniteQueryResult<AxiosResponse<{ next: string | null; results: FlowRecord[] }>>;

type StatusFilter = 'all' | RecordStatus;

const STATUS_META: Record<RecordStatus, { label: string; color: string }> = {
  success: { label: 'Success', color: 'green' },
  no_op: { label: 'No-op', color: 'gray' },
  failure: { label: 'Failed', color: 'red' },
  pending: { label: 'Pending', color: 'blue' },
};

const StatusIcon = ({ status }: { status: string }): JSX.Element => {
  if (status === 'success') return <IconCircleCheck size={20} color='green' />;
  if (status === 'no_op') return <IconCircleCheck size={20} color='gray' />;
  if (status === 'failure') return <IconCircleX size={20} color='red' />;
  return <Loader size={16} />;
};

const ParsedField = ({ label, value }: { label: string; value: string | null }): JSX.Element => (
  <Box>
    <Text size='xs' color='dimmed' transform='uppercase'>
      {label}
    </Text>
    <Text size='sm' style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
      {value ?? '-'}
    </Text>
  </Box>
);

const RecordRow = ({ record, timezoneName }: { record: FlowRecord; timezoneName: string }): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState(false);
  const parsed = parseRecordMessage(record.message);
  // Success, failure, and no-op rows all expand to show their parsed structured fields.
  const expandable = record.status === 'success' || record.status === 'failure' || record.status === 'no_op';

  const bg =
    record.status === 'failure'
      ? theme.colors.red[0]
      : record.status === 'no_op'
      ? theme.colors.gray[1]
      : record.status === 'success'
      ? theme.colors.green[0]
      : theme.white;

  const athleteName = `${record.athleteFirstName ?? ''} ${record.athleteLastName ?? ''}`.trim();
  const timestamp = dayjs(record.updatedAt).tz(timezoneName).format('MMM D, YYYY h:mm A');

  return (
    <Box
      style={{
        backgroundColor: bg,
        borderRadius: theme.radius.sm,
        border: `1px solid ${theme.colors.gray[2]}`,
      }}
    >
      <Group
        position='apart'
        noWrap
        p='xs'
        style={{ cursor: expandable ? 'pointer' : 'default' }}
        onClick={expandable ? () => setExpanded(value => !value) : undefined}
      >
        <Group noWrap spacing='sm' style={{ minWidth: 0 }}>
          <StatusIcon status={record.status} />
          <Box style={{ minWidth: 0 }}>
            {athleteName && record.athleteId ? (
              <Text
                size='sm'
                truncate
                underline
                sx={{ cursor: 'pointer' }}
                onClick={event => {
                  event.stopPropagation();
                  navigate(`/users/athletes/${record.athleteId}/general-info`);
                }}
              >
                {athleteName}
              </Text>
            ) : (
              <Text size='sm' truncate color={athleteName ? undefined : 'dimmed'}>
                {athleteName || 'Unknown athlete'}
              </Text>
            )}
            <Text size='xs' color='dimmed' truncate>
              {parsed?.detail ?? record.message ?? '-'}
            </Text>
          </Box>
        </Group>
        <Group noWrap spacing='xs'>
          <Text size='xs' color='dimmed'>
            {timestamp}
          </Text>
          {expandable && (expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />)}
        </Group>
      </Group>

      {expandable && (
        <Collapse in={expanded}>
          <Box px='xs' pb='xs'>
            <SimpleGrid cols={2} spacing='xs' verticalSpacing='xs'>
              <ParsedField label='Date & Time' value={timestamp} />
              <ParsedField
                label='Status'
                value={STATUS_META[record.status as RecordStatus]?.label ?? record.status}
              />
              <ParsedField label='Action' value={parsed?.action ?? null} />
              <ParsedField label='Trigger' value={parsed?.trigger ?? null} />
              <ParsedField label='Gym ID' value={parsed?.gymId ?? null} />
              <ParsedField label='Athlete ID' value={parsed?.athleteId ?? null} />
            </SimpleGrid>

            {parsed?.subSteps ? (
              <Box mt='xs'>
                <Text size='xs' color='dimmed' transform='uppercase'>
                  Sub-steps
                </Text>
                <Group spacing='xs' mt={4}>
                  {parsed.subSteps.map(step => (
                    <Badge
                      key={step.step}
                      color={/fail|error/i.test(step.result) ? 'red' : 'green'}
                      variant='light'
                    >
                      {step.step}: {step.result}
                    </Badge>
                  ))}
                </Group>
              </Box>
            ) : (
              <Box mt='xs'>
                <ParsedField label='Detail' value={parsed?.detail ?? null} />
              </Box>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const FlowRecordsModal = ({
  opened,
  onClose,
  flowId,
  flowLabel,
  direction,
  useRecords,
}: {
  opened: boolean;
  onClose: () => void;
  flowId: string | null;
  flowLabel?: string;
  direction: GLMFlowDirection;
  useRecords: UseRecordsFn;
}): JSX.Element => {
  const { session } = useAuth();
  const { trackGLMRecordsModalViewed, trackGLMRecordsModalFiltered } = useStaffTracking();
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useRecords(
    opened ? flowId : null,
  );

  const timezoneName = session?.gym?.timezone ?? 'UTC';
  const records = data?.pages.flatMap(page => page.data.results) ?? [];
  const visibleRecords =
    statusFilter === 'all' ? records : records.filter(record => record.status === statusFilter);

  React.useEffect(() => {
    if (opened && flowId) {
      trackGLMRecordsModalViewed({ flow_id: flowId, direction });
      setStatusFilter('all');
    }
  }, [opened, flowId, direction, trackGLMRecordsModalViewed]);

  const handleFilterChange = (value: StatusFilter): void => {
    setStatusFilter(value);
    if (flowId) trackGLMRecordsModalFiltered({ flow_id: flowId, direction, status: value });
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      title={<Title order={4}>{flowLabel ? `Records — ${flowLabel}` : 'Execution Records'}</Title>}
      size='90%'
    >
      <Stack spacing='sm'>
        <Group position='right'>
          <Select
            label='Status'
            size='xs'
            value={statusFilter}
            onChange={(value: StatusFilter | null) => handleFilterChange(value ?? 'all')}
            data={[
              { value: 'all', label: 'All' },
              { value: 'success', label: 'Success' },
              { value: 'no_op', label: 'No-op' },
              { value: 'failure', label: 'Failed' },
            ]}
          />
        </Group>

        <ScrollArea style={{ height: 460 }}>
          {isLoading ? (
            <Center h={440}>
              <Loader />
            </Center>
          ) : isError ? (
            <Center h={440}>
              <Text color='red'>Failed to load records</Text>
            </Center>
          ) : visibleRecords.length === 0 ? (
            <Center h={440}>
              <Text color='dimmed'>No records found</Text>
            </Center>
          ) : (
            <Stack spacing='xs' pr='sm'>
              {visibleRecords.map((record, index) => (
                <RecordRow
                  key={`${record.updatedAt}-${index}`}
                  record={record}
                  timezoneName={timezoneName}
                />
              ))}
              {hasNextPage && (
                <Center py='sm'>
                  <Button
                    variant='subtle'
                    size='xs'
                    loading={isFetchingNextPage}
                    onClick={() => fetchNextPage()}
                  >
                    Load more
                  </Button>
                </Center>
              )}
            </Stack>
          )}
        </ScrollArea>
      </Stack>
    </Modal>
  );
};

export default FlowRecordsModal;
